import { action, computed, makeObservable, observable, runInAction, when } from 'mobx'
import { RootStore } from './RootStore'
import { StoreModel } from './utils'
import { CommerceAPI } from 'commerce-api'
import { formatNumberToCurrency } from '../utils/utils'
import { StatusMap } from '../types/commerce'
import { getAppOrigin } from '@salesforce/pwa-kit-react-sdk/utils/url'
import { AvailableDeliveryMethod, DeliveryType, DeliveryTypes } from '../types/delivery'

const POSTCODE_WAIT_TIMEOUT = 10000

export class DeliveryStore implements StoreModel {
    api: CommerceAPI
    rootStore: RootStore
    isPreBookEnabled: boolean
    isPreBookForNewCustomersEnabled: boolean
    deliveryMethods: AvailableDeliveryMethod[] | null
    deliveryMethodsPostcode: string | undefined
    isDeliverySlotsLoading: boolean
    isPreBookAvailabilityLoading: boolean
    deliverySlotsError: boolean
    preBookAvailabilityError: boolean
    pendingAvailability: Promise<void> | null = null
    pendingAvailabilityPostcode: string | undefined
    pendingAvailabilityKey: string | null = null

    constructor(
        rootStore: RootStore,
        initialState?: Pick<DeliveryStore, 'isPreBookEnabled' | 'isPreBookForNewCustomersEnabled'>
    ) {
        this.api = rootStore.api
        this.rootStore = rootStore
        this.isPreBookEnabled = initialState?.isPreBookEnabled ?? false
        this.isPreBookForNewCustomersEnabled = initialState?.isPreBookForNewCustomersEnabled ?? false
        this.isDeliverySlotsLoading = true
        this.isPreBookAvailabilityLoading = true
        this.deliverySlotsError = false
        this.preBookAvailabilityError = false
        this.deliveryMethods = null

        makeObservable(this, {
            isPreBookEnabled: observable,
            isPreBookForNewCustomersEnabled: observable,
            isDeliverySlotsLoading: observable,
            isPreBookAvailabilityLoading: observable,
            deliverySlotsError: observable,
            preBookAvailabilityError: observable,
            deliveryMethods: observable,
            availableDeliveryTypes: computed,
            minDeliveryThreshold: computed,
            setPreBookEnabled: action.bound,
            setPreBookForNewCustomersEnabled: action.bound,
            setDeliverySlotsLoading: action.bound,
            setPreBookAvailabilityLoading: action.bound,
            setDeliverySlotsError: action.bound,
            setPreBookAvailabilityError: action.bound,
            loadAvailability: action.bound
        })
    }

    get minDeliveryThreshold(): number | null {
        return (this.rootStore.globalStore.customSitePreferences['minimumDeliveryValue'] as number) ?? null
    }

    get availableDeliveryTypes(): DeliveryType[] {
        // Delivery methods is not yet loaded
        if (this.deliveryMethods === null) {
            return []
        }
        const { deliverySlotArray, sameDayDeliveryAvailable, isAppliedPostCodeDeliverable, shippingAddress, deliveryPostalCode } = this.rootStore.basketStore
        const { preBookDeliverySlotArray, preBookDeliverySlots, preBookDays, preBookSeasonalName, prebookedOrder } =
            this.rootStore.preBookStore
        const { 
            isBasketOverRapidDeliveryItemLimit,
            rapidDeliveryMaxItems,
            isRapidDeliveryAvailableForPostcode,
            rapidDeliveryMinDelivery,
            rapidDeliveryDeliveryEstimate,
            rapidDeliveryShippingCost,
            isBasketOverRapidDeliveryWeightLimit,
            rapidDeliveryAvailabilityResolved,
            status: rapidDeliveryStatus
        } = this.rootStore.rapidDeliveryStore
        const standardDeliveryAvailable = isAppliedPostCodeDeliverable && deliverySlotArray.some((day) => day.hasAvailableSlots)
        const preBookDatesReturned = Object.keys(preBookDeliverySlots || {}).length > 0
        const preBookDeliveryAvailable =
            this.isPreBookEnabled &&
            this.isPreBookForNewCustomersEnabled &&
            isAppliedPostCodeDeliverable === true &&
            preBookDatesReturned &&
            preBookDeliverySlotArray.some((day) => day.hasAvailableSlots)
        const getSlotCost = (slot: (typeof deliverySlotArray)[number]['slots'][number]) =>
            slot.hasFreeDelivery ? 0 : slot.shippingCost
        const standardCosts = deliverySlotArray.flatMap((day) =>
            day.hasAvailableSlots ? day.slots.filter((slot) => slot.available).map(getSlotCost) : []
        )
        const sameDayCosts = (deliverySlotArray[0]?.slots || [])
            .filter((slot) => slot.available)
            .map(getSlotCost)
        const preBookCosts = preBookDeliverySlotArray.flatMap((day) =>
            day.hasAvailableSlots
                ? day.slots
                    .filter((slot) => slot.available)
                    .map((slot) => slot.preBookShippingCost ?? slot.shippingCost)
                : []
        )
        const lowest = (costs: number[]) => (costs.length ? Math.min(...costs) : null)
        const methodData: DeliveryType[] = [
            {
                deliveryType: DeliveryTypes.RAPID,
                title: 'Rapid Delivery',
                available:
                    isRapidDeliveryAvailableForPostcode 
                    && !isBasketOverRapidDeliveryItemLimit 
                    && !isBasketOverRapidDeliveryWeightLimit
                    || false,
                lowestCost: rapidDeliveryShippingCost,
                unavailableMessage:
                    isBasketOverRapidDeliveryItemLimit || isBasketOverRapidDeliveryWeightLimit
                    ? 'Your current basket is over the limit for Rapid Delivery'
                    : null,
                deliveryTypeSubtext: `Delivered in under ${rapidDeliveryDeliveryEstimate ?? 60} minutes`,
                features: [
                    'Delivered in under 1 hour',
                    `Up to ${rapidDeliveryMaxItems} items per order`,
                    `Minimum ${formatNumberToCurrency(rapidDeliveryMinDelivery || 0, 'GBP', 0, 2)} spend per order`
                ],
                loading: !rapidDeliveryAvailabilityResolved && rapidDeliveryStatus === StatusMap.PENDING
            },
            {
                deliveryType: DeliveryTypes.SAMEDAY,
                title: 'Same Day Delivery',
                available: !this.deliverySlotsError && (sameDayDeliveryAvailable || false),
                lowestCost: lowest(sameDayCosts),
                unavailableMessage: null,
                deliveryTypeSubtext: 'Get your shop delivered today',
                features: [
                    'Need it today? Get it today',
                    'Flexible 2-hour delivery slots',
                    'No basket limits'
                ],
                loading: this.isDeliverySlotsLoading
            },
            {
                deliveryType: DeliveryTypes.STANDARD,
                title: 'Standard Delivery',
                available: !this.deliverySlotsError && (standardDeliveryAvailable || false),
                lowestCost: lowest(standardCosts),
                unavailableMessage: null,
                deliveryTypeSubtext: "1000's of delivery slots to suit you",
                features: [
                    'Next Day Delivery',
                    'Flexible 2 hour delivery slots',
                    'Book up to 7 days in advance'
                ],
                loading: this.isDeliverySlotsLoading
            },
            {
                deliveryType: DeliveryTypes.PREBOOK,
                title: 'Pre-Book Delivery',
                available: !this.preBookAvailabilityError && (preBookDeliveryAvailable || false),
                lowestCost: lowest(preBookCosts),
                unavailableMessage: null,
                deliveryTypeSubtext: `Pre-book your ${preBookSeasonalName && `${preBookSeasonalName} `}delivery up to ${preBookDays} days in advance`,
                features: [
                    `Pre-book slots up to ${preBookDays} days in advance`,
                    'Plan ahead for holidays and parties',
                    'Secure your delivery slot'
                ],
                loading: this.isPreBookAvailabilityLoading
            }
        ]

        const availableMethods = methodData.filter(
            (delivery) => {
                const RapidAvailable =
                    delivery.deliveryType !== DeliveryTypes.RAPID ||
                    (rapidDeliveryAvailabilityResolved && isRapidDeliveryAvailableForPostcode &&
                        !!shippingAddress?.postalCode && shippingAddress.postalCode === deliveryPostalCode)
                const PreBookAvailable = delivery.deliveryType !== DeliveryTypes.PREBOOK || !prebookedOrder
                // Filter out unavailable delivery options
                return RapidAvailable && PreBookAvailable
            }
        )

        const deliveryMethods: AvailableDeliveryMethod[] = this.deliveryMethods.length > 0
            ? this.deliveryMethods
            : methodData.map((method) => ({ ...method, type: method.deliveryType }))

        return deliveryMethods.flatMap((apiMethod) => {
            const method = availableMethods.find(
                (method) => method.deliveryType === apiMethod.type
            )

            return method ? [{
                ...method,
                sortOrder: apiMethod.sortOrder,
                icon: apiMethod.icon || null,
                ctaText: apiMethod.ctaText || 'Book a Slot',
                backgroundImage: apiMethod.backgroundImage || '',
                theme: apiMethod.theme || 'light'
            }] : []
        })
    }

    get asJson() {
        return {
            isPreBookEnabled: this.isPreBookEnabled,
            isPreBookForNewCustomersEnabled: this.isPreBookForNewCustomersEnabled
        }
    }

    setPreBookEnabled(enabled: boolean) {
        this.isPreBookEnabled = enabled
    }

    setPreBookForNewCustomersEnabled(enabled: boolean) {
        this.isPreBookForNewCustomersEnabled = enabled
    }

    setDeliverySlotsLoading(loading: boolean) {
        this.isDeliverySlotsLoading = loading
    }

    setPreBookAvailabilityLoading(loading: boolean) {
        this.isPreBookAvailabilityLoading = loading
    }

    setDeliverySlotsError(error: boolean) {
        this.deliverySlotsError = error
    }

    setPreBookAvailabilityError(error: boolean) {
        this.preBookAvailabilityError = error
    }

    async loadAvailability(): Promise<void> {
        const postcode = this.rootStore.basketStore.deliveryPostalCode
        if (this.pendingAvailability && this.pendingAvailabilityPostcode === postcode) {
            return this.pendingAvailability
        }

        const pendingAvailability = this.fetchAvailability().finally(() => {
            runInAction(() => {
                if (this.pendingAvailability !== pendingAvailability) return
                this.pendingAvailability = null
                this.pendingAvailabilityPostcode = undefined
            })
        })
        this.pendingAvailability = pendingAvailability
        this.pendingAvailabilityPostcode = postcode

        return this.pendingAvailability
    }

    private async fetchAvailability(): Promise<void> {
        const { basketStore, customerStore, globalStore, preBookStore } = this.rootStore

        if (!basketStore.basket?.basketId) return

        this.setDeliverySlotsLoading(true)
        this.setPreBookAvailabilityLoading(true)
        this.setDeliverySlotsError(false)
        this.setPreBookAvailabilityError(false)

        // Preferences do not need the postcode, so fetch them while it resolves
        const preferencesPromise = globalStore.getCustomSitePreferencesByIds([
            'preBookEnabled',
            'preBookForNewCustomersEnabled'
        ]) as unknown as Promise<unknown>

        // The postcode resolves asynchronously, so wait for it before fetching anything that depends on it
        if (!basketStore.deliveryPostalCode) {
            try {
                await when(() => Boolean(basketStore.deliveryPostalCode), {
                    timeout: POSTCODE_WAIT_TIMEOUT
                })
            } catch (error) {
                console.error('Timed out waiting for a delivery postcode', error)
            }
        }

        const postcode = basketStore.deliveryPostalCode

        const slotsPromise = (async () => {
            try {
                if (postcode) await (basketStore.getDeliverySlots() as unknown as Promise<unknown>)
            await this.fetchAvailableDeliveryMethods(postcode)
            } catch (error) {
                this.setDeliverySlotsError(true)
                console.error('Error fetching delivery slots', error)
            } finally {
                this.setDeliverySlotsLoading(false)
            }
        })()

        const preBookPromise = (async () => {
            try {
                await preferencesPromise
                this.setPreBookEnabled(Boolean(globalStore.customSitePreferences.preBookEnabled))
                this.setPreBookForNewCustomersEnabled(
                    Boolean(globalStore.customSitePreferences.preBookForNewCustomersEnabled)
                )

                if (this.isPreBookEnabled && postcode) {
                    // Loading the customer can trigger resetPreBook(), which clears the pre-book postcode mid-fetch
                    if (customerStore.loading) {
                        try {
                            await when(() => !customerStore.loading, { timeout: POSTCODE_WAIT_TIMEOUT })
                        } catch (error) {
                            console.error('Timed out waiting for the customer to load', error)
                        }
                    }

                    await (preBookStore.setPreBookPostalCode(postcode) as unknown as Promise<unknown>)
                    await (preBookStore.getPreBookDeliverySlots() as unknown as Promise<unknown>)
                }
            } catch (error) {
                this.setPreBookAvailabilityError(true)
                console.error('Error fetching pre-book delivery slots', error)
            } finally {
                this.setPreBookAvailabilityLoading(false)
            }
        })()

        await Promise.all([slotsPromise, preBookPromise])
    }

    async fetchAvailableDeliveryMethods(postcode: string): Promise<void> {
        if (this.deliveryMethodsPostcode === postcode) return

        runInAction(() => {
            this.deliveryMethods = []
            this.deliveryMethodsPostcode = postcode
        })

        try {
            const response = await fetch(`${getAppOrigin()}/api/getAvailableDeliveryMethods`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Authorization: this.api.auth.authToken
                }
            })

            if (!response.ok) {
                throw new Error(`Unable to get available delivery methods: ${response.status}`)
            }

            const {deliveryMethods}: {deliveryMethods?: AvailableDeliveryMethod[]} = await response.json()

            if (this.rootStore.basketStore.deliveryPostalCode !== postcode) return

            runInAction(() => {
                this.deliveryMethods = deliveryMethods ?? []
            })
        } catch (error) {
            if (this.rootStore.basketStore.deliveryPostalCode === postcode) {
                runInAction(() => {
                    this.deliveryMethods = []
                })
            }
            console.error('Error fetching available delivery methods', error)
        }
    }
}
